import React, { useState, useEffect } from 'react';
import { FaUserEdit, FaTimes, FaSave } from 'react-icons/fa';
import { addDoc, setDoc, doc, collection } from 'firebase/firestore';
import { db } from '../../config/firebaseConfig';
import { toast } from 'react-toastify';
import styles from './EditCustomerModal.module.css';
import { useTheme } from '../../contexts/ThemeContext';

const EditCustomerModal = ({
  isModalOpen,
  setIsModalOpen,
  fetchCustomers,
  selectedCustomer,
  setSelectedCustomer,
}) => {
  const { isDarkMode } = useTheme();
  const [newCustomer, setNewCustomer] = useState({
    'Müşteri Adı': '',
    'Bilgi': '',
    'Ana Anlaşma Numarası': '',
    'Ana Anlaşma Tarihi': '',
    'Durum': 'aktif',
    'Döviz Tipi': 'TRY',
    'Müşteri Hizmet Num.': '',
    'Teslim Tarihi': '',
    'Vergi Numarası': '',
    'Yurtiçi mi?': 'Evet',
    'Ödeme Periyodu': '',
  });
  const [isLoading, setIsLoading] = useState(false);
  const [formErrors, setFormErrors] = useState({});

  useEffect(() => {
    if (selectedCustomer) {
      setNewCustomer({
        'Müşteri Adı': selectedCustomer['Başlık'] || selectedCustomer['Müşteri Adı'] || '',
        'Bilgi': selectedCustomer['Bilgi'] || '',
        'Ana Anlaşma Numarası': selectedCustomer['Ana Anlaşma Numarası'] || '',
        'Ana Anlaşma Tarihi': selectedCustomer['Ana Anlaşma Tarihi'] || '',
        'Durum': selectedCustomer['Durum'] || 'aktif',
        'Döviz Tipi': selectedCustomer['Döviz Tipi'] || 'TRY',
        'Müşteri Hizmet Num.': selectedCustomer['Müşteri Hizmet Num.'] || '',
        'Teslim Tarihi': selectedCustomer['Teslim Tarihi'] || '',
        'Vergi Numarası': selectedCustomer['Vergi Numarası'] || '',
        'Yurtiçi mi?': selectedCustomer['Yurtiçi mi?'] || 'Evet',
        'Ödeme Periyodu': selectedCustomer['Ödeme Periyodu'] || 30,
      });
    }
  }, [selectedCustomer]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    // Convert "Müşteri Adı" to uppercase
    const processedValue = name === 'Müşteri Adı' ? value.toUpperCase() : value;
    setNewCustomer((prevState) => ({
      ...prevState,
      [name]: processedValue,
    }));
    
    // Clear error when user starts typing
    if (formErrors[name]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const errors = {};
    
    if (!newCustomer['Müşteri Adı'] || newCustomer['Müşteri Adı'].trim() === '') {
      errors['Müşteri Adı'] = 'Müşteri adı zorunludur';
    }
    
    if (newCustomer['Ödeme Periyodu'] && (isNaN(newCustomer['Ödeme Periyodu']) || newCustomer['Ödeme Periyodu'] < 0)) {
      errors['Ödeme Periyodu'] = 'Ödeme periyodu geçerli bir sayı olmalıdır';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedCustomer(null);
  };

  const addCustomerToFirestore = async () => {
    if (!validateForm()) {
      toast.error('Lütfen form hatalarını düzeltin');
      return;
    }
    
    setIsLoading(true);
    try {
      const customerData = {
        ...newCustomer,
        'Başlık': newCustomer['Müşteri Adı'], // Başlık alanını da ekle
        'Oluşturma Tarihi': new Date().toISOString(),
        'Güncelleme Tarihi': new Date().toISOString()
      };
      
      await addDoc(collection(db, 'Customers'), customerData);
      await fetchCustomers();
      toast.success('Yeni müşteri başarıyla eklendi!');
      closeModal();
    } catch (error) {
      console.error('Error adding new customer:', error);
      toast.error(`Yeni müşteri eklenemedi: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  const updateCustomerInFirestore = async () => {
    if (!validateForm()) {
      toast.error('Lütfen form hatalarını düzeltin');
      return;
    }
    
    if (!selectedCustomer) {
      toast.error('Güncellenecek müşteri bulunamadı');
      return;
    }
    
    setIsLoading(true);
    try {
      const customerData = {
        ...newCustomer,
        'Başlık': newCustomer['Müşteri Adı'], // Başlık alanını da güncelle
        'Güncelleme Tarihi': new Date().toISOString()
      };
      
      const customerRef = doc(db, 'Customers', selectedCustomer.id);
      await setDoc(customerRef, customerData, { merge: true });
      await fetchCustomers();
      toast.success('Müşteri başarıyla güncellendi!');
      closeModal();
    } catch (error) {
      console.error('Error updating customer:', error);
      toast.error(`Müşteri güncellenemedi: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  // Add overlay click handler
  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      closeModal();
    }
  };

  return (
    <div 
      className={styles.modalOverlay} 
      onClick={handleOverlayClick}
    >
      <div className={styles.modalContent}>
        <div className={styles.modalHeader}>
          <h2 className={styles.modalTitle}>
            <FaUserEdit className={styles.modalIcon} />
            {selectedCustomer ? 'Müşteriyi Düzenle' : 'Yeni Müşteri Ekle'}
          </h2>
          <button 
            className={styles.closeButton} 
            onClick={closeModal}
            aria-label="Kapat"
          >
            <FaTimes />
          </button>
        </div>
        
        <div className={styles.modalBody}>
          <form onSubmit={(e) => e.preventDefault()} id="edit-customer-form">
            <div className={styles.formGrid}>
              <div className={styles.formGroup}>
                <label className={styles.formLabel}>Müşteri Adı:</label>
                <input
                  type="text"
                  name="Müşteri Adı"
                  value={newCustomer['Müşteri Adı']}
                  onChange={handleInputChange}
                  required
                  className={`${styles.formInput} ${formErrors['Müşteri Adı'] ? styles.formInputError : ''}`}
                />
                {formErrors['Müşteri Adı'] && (
                  <span className={styles.errorMessage}>{formErrors['Müşteri Adı']}</span>
                )}
              </div>

              <div className={styles.formGroup}>
                <label className={styles.formLabel}>Bilgi:</label>
                <input
                  type="text"
                  name="Bilgi"
                  value={newCustomer['Bilgi']}
                  onChange={handleInputChange}
                  className={styles.formInput}
                />
              </div>

              <div className={styles.formGroup}>
                <label className={styles.formLabel}>Ana Anlaşma Numarası:</label>
                <input
                  type="text"
                  name="Ana Anlaşma Numarası"
                  value={newCustomer['Ana Anlaşma Numarası']}
                  onChange={handleInputChange}
                  className={styles.formInput}
                />
              </div>

              <div className={styles.formGroup}>
                <label className={styles.formLabel}>Ana Anlaşma Tarihi:</label>
                <input
                  type="date"
                  name="Ana Anlaşma Tarihi"
                  value={newCustomer['Ana Anlaşma Tarihi']}
                  onChange={handleInputChange}
                  className={styles.formInput}
                />
              </div>

              <div className={styles.formGroup}>
                <label className={styles.formLabel}>Durum:</label>
                <select
                  name="Durum"
                  value={newCustomer['Durum']}
                  onChange={handleInputChange}
                  className={styles.formSelect}
                >
                  <option value="aktif">Aktif</option>
                  <option value="inaktif">İnaktif</option>
                </select>
              </div>

              <div className={styles.formGroup}>
                <label className={styles.formLabel}>Döviz Tipi:</label>
                <select
                  name="Döviz Tipi"
                  value={newCustomer['Döviz Tipi']}
                  onChange={handleInputChange}
                  className={styles.formSelect}
                >
                  <option value="TRY">TRY</option>
                  <option value="USD">USD</option>
                  <option value="EUR">EUR</option>
                </select>
              </div>

              <div className={styles.formGroup}>
                <label className={styles.formLabel}>Müşteri Hizmet Numarası:</label>
                <input
                  type="text"
                  name="Müşteri Hizmet Num."
                  value={newCustomer['Müşteri Hizmet Num.']}
                  onChange={handleInputChange}
                  className={styles.formInput}
                />
              </div>

              <div className={styles.formGroup}>
                <label className={styles.formLabel}>Sözleşme Bitiş Tarihi:</label>
                <input
                  type="date"
                  name="Teslim Tarihi"
                  value={newCustomer['Teslim Tarihi']}
                  onChange={handleInputChange}
                  className={styles.formInput}
                />
              </div>

              <div className={styles.formGroup}>
                <label className={styles.formLabel}>Vergi Numarası:</label>
                <input
                  type="text"
                  name="Vergi Numarası"
                  value={newCustomer['Vergi Numarası']}
                  onChange={handleInputChange}
                  className={styles.formInput}
                />
              </div>

              <div className={styles.formGroup}>
                <label className={styles.formLabel}>Yurtiçi mi?</label>
                <select
                  name="Yurtiçi mi?"
                  value={newCustomer['Yurtiçi mi?']}
                  onChange={handleInputChange}
                  className={styles.formSelect}
                >
                  <option value="Evet">Evet</option>
                  <option value="Hayır">Hayır</option>
                </select>
              </div>

              <div className={styles.formGroup}>
                <label className={styles.formLabel}>Ödeme Periyodu:</label>
                <input
                  type="number"
                  name="Ödeme Periyodu"
                  value={newCustomer['Ödeme Periyodu']}
                  onChange={handleInputChange}
                  className={`${styles.formInput} ${formErrors['Ödeme Periyodu'] ? styles.formInputError : ''}`}
                />
                {formErrors['Ödeme Periyodu'] && (
                  <span className={styles.errorMessage}>{formErrors['Ödeme Periyodu']}</span>
                )}
              </div>
            </div>
          </form>
        </div>
        
        <div className={styles.formActions}>
          <button 
            type="button" 
            className={styles.buttonCancel} 
            onClick={closeModal}
          >
            İptal
          </button>
          <button 
            type="button" 
            onClick={selectedCustomer ? updateCustomerInFirestore : addCustomerToFirestore}
            className={styles.buttonSave}
            disabled={isLoading}
          >
            <FaSave className={styles.buttonIcon} />
            {isLoading ? 'Kaydediliyor...' : (selectedCustomer ? 'Güncelle' : 'Kaydet')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default EditCustomerModal;
